import * as React from 'react';
import { cloneExcept } from 'core/utils';
import { Form, FormComponent } from './Form';
import { TextBox, TextBoxButton, TextBoxCtor } from './TextBox';
import * as Dialog from './Dialog';
import { LanguageNames, Result } from 'core/infrastructure';
import { DialogActions } from './Dialog';
import ConfirmDialog from './ConfirmDialog';
import { Column } from './Grid';
import { Res } from 'core/api';

type Translation = { LanguageCode: string, Translation: string }
type TranslatableString = { DefaultValue: string; Translations: Translation[]; }

function isTranslation(value: any): value is Translation {
	const maybeTranslation = (value as Translation);
	return maybeTranslation.LanguageCode !== undefined && maybeTranslation.Translation !== undefined;
}

function isTranslatableString(value: any): value is TranslatableString {

	const maybeTranslatableString = (value as TranslatableString);
	if (maybeTranslatableString.DefaultValue !== undefined && maybeTranslatableString.Translations !== undefined) {
		return maybeTranslatableString.Translations.length === 0 || isTranslation(maybeTranslatableString.Translations[0]);
	}

	return false;
}

type TranslationDialogProps = {
	translations: Translation[],
	actions?: DialogActions<Translation[]>
};

type TranslationDialogState = {
	translations: Translation[]
};

class TranslationDialog extends React.Component<TranslationDialogProps, TranslationDialogState> {

	private form = React.createRef<Form<TranslationDialogState>>();

	constructor(props: TranslationDialogProps) {
		super(props);
		this.state = { translations: [...props.translations] };
	}

	render() {

		const onCancel = () => this.props.actions?.cancel();
		const onOK = async () => {

			const updated = await this.form.current!.updateModel();
			if (!updated)
				return;

			this.props.actions?.closeWith(this.state.translations);
		};

		return (
			<ConfirmDialog title={Res.WA.WA_LOCALIZABLE_STRING_DIALOG_TITLE()} onCancel={onCancel} onOK={onOK} >
				<Form<TranslationDialogState> ref={this.form} model={this.state}>
					<Column width='full'>
						{this.state.translations.map((t, i) => {
							return <TextBox linkTo={`translations[${i}].Translation`} key={i} label={`${LanguageNames.getName(t.LanguageCode)}:`} />;
						})}
					</Column>
				</Form>
			</ConfirmDialog >
		);
	}
}

interface Props<T> extends React.DetailedHTMLProps<React.InputHTMLAttributes<TranslatableTextBox<T>>, TranslatableTextBox<T>> {
	syncTextFrom?: string;
	onValueChange?: () => void;
	linkTo: (model: T) => TranslatableString;
	label?: string | React.ReactNode;
	boundValue?: TranslatableString;
}

export class TranslatableTextBox<T> extends React.Component<Props<T>, TranslatableString> implements FormComponent {

	constructor(props: Props<T>) {
		super(props);

		if (!isTranslatableString(props.boundValue))
			throw new Error('TranslatableTextBox esperaba un bound value de tipo TranstableString');

		this.state = {
			DefaultValue: props.boundValue.DefaultValue,
			Translations: props.boundValue.Translations.map(t => ({ ...t }))
		};
	}

	private textBox = React.createRef<TextBox<T>>();

	getPath() {
		return this.textBox.current!.getPath();
	}

	showErrors(errors: string[]) {
		this.textBox.current!.showErrors(errors);
	}

	getValue() {
		return Result.ok(this.state);
	}

	setValue(value: number) {
		this.textBox.current!.setValue(value);
	}

	activate() {
		this.textBox.current!.activate();
	}

	render() {

		const TheTextBox: TextBoxCtor<T> = TextBox;
		const onEditTranslations = async () => {
			const translationsResult = await Dialog.showCustom<Translation[]>(<TranslationDialog translations={this.state.Translations} />);
			if (translationsResult.ok)
				this.setState({ Translations: translationsResult.value });
		};

		const onDefaultValueChanged = (value: string) => this.setState({ DefaultValue: value }, () => this.props.onValueChange?.());

		const props = cloneExcept(this.props, ['ref']);
		props.type = 'text';

		return this.props.boundValue?.Translations.length
			? <TheTextBox ref={this.textBox} {...props} boundValue={this.state.DefaultValue} onValueChange={onDefaultValueChanged}>
				<TextBoxButton iconClassName='text-info fa fa-globe' onClick={onEditTranslations} />
			</TheTextBox>
			: <TheTextBox ref={this.textBox} {...props} boundValue={this.state.DefaultValue} onValueChange={onDefaultValueChanged} />;
	}
}

export type TranslatableTextBoxCtor<T> = new (props: Props<T>) => TranslatableTextBox<T>;

